import * as React from 'react';
import * as AvatarPrimitive from '@radix-ui/react-avatar';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

/**
 * Sober DS (v2) — Avatar. Built on @radix-ui/react-avatar. Uniform rounded-full
 * shape (no colored borders); emphasis comes from a tinted gradient fallback,
 * not an accent border. Sizes sm/md/lg/xl follow the Sober scale.
 */
const avatarVariants = cva('relative flex shrink-0 overflow-hidden rounded-full', {
  variants: {
    size: {
      sm: 'h-8 w-8 text-so-12',
      md: 'h-10 w-10 text-so-14',
      lg: 'h-14 w-14 text-so-20',
      xl: 'h-[72px] w-[72px] text-so-28',
    },
  },
  defaultVariants: { size: 'md' },
});

const GRADIENTS: Record<string, string> = {
  g1: 'linear-gradient(135deg, #fea42d 0%, #f5647a 100%)',
  g2: 'linear-gradient(135deg, #7CE7AC 0%, #3FC78B 100%)',
  g3: 'linear-gradient(135deg, #F4BE5E 0%, #E0A93B 100%)',
  g4: 'linear-gradient(135deg, #FF808B 0%, #E85D68 100%)',
  g5: 'linear-gradient(135deg, #9698D6 0%, #7B7DC0 100%)',
};

type Gradient = keyof typeof GRADIENTS;

const BLOCKED_REMOTE_AVATAR_HOSTS = new Set(['pps.whatsapp.net']);

function sanitizeAvatarSrc(src?: string) {
  if (!src) return undefined;
  try {
    const url = new URL(src);
    if (BLOCKED_REMOTE_AVATAR_HOSTS.has(url.hostname)) return undefined;
  } catch {
    return src;
  }
  return src;
}

export interface AvatarProps
  extends React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Root>,
    VariantProps<typeof avatarVariants> {
  src?: string;
  alt?: string;
  fallback: string;
  gradient?: Gradient;
}

export const Avatar = React.forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Root>,
  AvatarProps
>(({ className, size, src, alt, fallback, gradient = 'g1', ...props }, ref) => {
  const safeSrc = sanitizeAvatarSrc(src);

  return (
    <AvatarPrimitive.Root
      ref={ref}
      className={cn(avatarVariants({ size }), className)}
      {...props}
    >
      {safeSrc && (
        <AvatarPrimitive.Image
          className="aspect-square h-full w-full object-cover"
          src={safeSrc}
          alt={alt}
        />
      )}
      <AvatarPrimitive.Fallback
        className="flex h-full w-full items-center justify-center font-bold text-white"
        style={{ background: GRADIENTS[gradient] }}
      >
        {fallback}
      </AvatarPrimitive.Fallback>
    </AvatarPrimitive.Root>
  );
});
Avatar.displayName = 'Avatar';

export { avatarVariants, sanitizeAvatarSrc };
